import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import analyticsService from '../../services/analyticsService';

/**
 * Centralized Sign Up button that shows only for:
 * - Non-logged-in users (navigates to /register)
 * 
 * Hidden when:
 * - User is logged in
 * - On the register page
 * - User already has subscription access
 */
const JoinBetaButton = ({ variant = 'default', className = '' }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const isRegisterPage = location.pathname === '/register';
  const hasAccess = user?.subscriptionStatus?.hasAccess === true;
  const isSuperuser = user?.superuser === true;
  const isLoggedIn = !!user;
  
  // Don't show if logged in, on register page, or if user has access
  if (isLoggedIn || isRegisterPage || (user && (hasAccess || isSuperuser))) {
    return null;
  }
  
  // Determine navigation target and button text
  const getNavigationTarget = () => {
    return '/register';
  };
  
  const getButtonText = () => 'Enforce Your Rules';
 
  const getAnalyticsEvent = () => {
    return 'signup_button_clicked';
  };
  
  // Landing page variant (larger, more prominent)
  if (variant === 'landing') {
    return (
      <button
        onClick={() => {
          // Store current UTM params before navigating (in case they're in URL)
          analyticsService.extractSourceParameters(window.location.search, true);
          
          // Get landing page version for A/B test tracking
          const landingVersion = sessionStorage.getItem('landing_page_version') || 'unknown';
          
          // trackTradingEvent will automatically include source from sessionStorage
          analyticsService.trackTradingEvent(getAnalyticsEvent(), {
            variant: 'landing',
            user_id: null,
            is_logged_in: false,
            landing_page_version: landingVersion
          });
          navigate(getNavigationTarget());
        }}
        className={`landing-cta-btn landing-cta-btn--nav ${className}`.trim()}
      >
        {getButtonText()}
      </button>
    );
  }
  
  // Header desktop variant — in-app header (Header.js on /trade etc.); stays on the app's purple brand.
  if (variant === 'header-desktop') {
    return (
      <button
        onClick={() => {
          // Store current UTM params before navigating (in case they're in URL)
          analyticsService.extractSourceParameters(window.location.search, true);
          
          // Get landing page version for A/B test tracking
          const landingVersion = sessionStorage.getItem('landing_page_version') || 'unknown';
          
          // trackTradingEvent will automatically include source from sessionStorage
          analyticsService.trackTradingEvent(getAnalyticsEvent(), {
            variant: 'header-desktop',
            user_id: null,
            is_logged_in: false,
            landing_page_version: landingVersion
          });
          navigate(getNavigationTarget());
        }}
        className={`d-none d-md-inline-flex ${className}`}
        style={{
          background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
          color: '#fff',
          border: 'none',
          padding: '8px 20px',
          borderRadius: '50px',
          fontWeight: '600',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)',
          alignItems: 'center',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
          marginLeft: 'auto',
          marginRight: '1rem'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.2)';
        }}
      >
        {getButtonText()}
      </button>
    );
  }
  
  // Header mobile variant
  if (variant === 'header-mobile') {
    return (
      <button
        onClick={() => {
          // Store current UTM params before navigating (in case they're in URL)
          analyticsService.extractSourceParameters(window.location.search, true);
          
          // Get landing page version for A/B test tracking
          const landingVersion = sessionStorage.getItem('landing_page_version') || 'unknown';
          
          // trackTradingEvent will automatically include source from sessionStorage
          analyticsService.trackTradingEvent(getAnalyticsEvent(), {
            variant: 'header-mobile',
            user_id: null,
            is_logged_in: false,
            landing_page_version: landingVersion
          });
          navigate(getNavigationTarget());
        }}
        className={`d-md-none ${className}`}
        style={{
          background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
          color: '#fff',
          border: 'none',
          padding: '6px 14px',
          borderRadius: '50px',
          fontWeight: '600',
          fontSize: '13px',
          cursor: 'pointer',
          marginLeft: 'auto',
          marginRight: '8px',
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)'
        }}
      >
        {getButtonText()}
      </button>
    );
  }
  
  // Default variant
  return (
    <button
      onClick={() => {
        // Store current UTM params before navigating (in case they're in URL)
        analyticsService.extractSourceParameters(window.location.search, true);
        
        // Get landing page version for A/B test tracking
        const landingVersion = sessionStorage.getItem('landing_page_version') || 'unknown';
        
        // trackTradingEvent will automatically include source from sessionStorage
        analyticsService.trackTradingEvent(getAnalyticsEvent(), {
          variant: 'default',
          user_id: null,
          is_logged_in: false,
          landing_page_version: landingVersion
        });
        navigate(getNavigationTarget());
      }}
      className={className}
      style={{
        background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
        color: '#fff',
        border: 'none',
        padding: '8px 20px',
        borderRadius: '50px',
        fontWeight: '600',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)',
        whiteSpace: 'nowrap'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.2)';
      }}
    >
      {getButtonText()}
    </button>
  );
};

export default JoinBetaButton;

















